/**
 * Ein Punkt des Berichts auf eigener Seite: Text, Verlauf seines Messpunkts,
 * die Zahlen, auf die er sich stuetzt. Blaettern ohne Umweg ueber die
 * Uebersicht, damit sich ein Bericht Punkt fuer Punkt durchgehen laesst.
 */
import { useState } from 'react'
import { Legende, Saeulen } from './Saeulen'
import { gruppeName, type Punkt, type Saeulen as SaeulenDaten } from './typen'

export function stand(p: Punkt, vorperiode: string): string {
  return p.neu ? 'neu' : p.seit ? `seit ${p.seit}` : `aus dem ${vorperiode}`
}

/** Was eine Saeule zeigt; der Zeitraum steht schon im Namen des Messpunkts ("lfd. Jahr", "12 Monate"). */
const VERLAUF_ART = { bestand: 'Stand am Monatsende', zeitraum: 'Wert', quote: 'Quote' } as const

type Sicht = 'jePeriode' | 'aufgelaufen' | 'gemessen'

/**
 * Der Verlauf eines Punkts. Bei Betraegen gibt es ihn je Monat und
 * aufgelaufen seit Januar, gleich wie die KI gemessen hat; zuerst je Monat,
 * weil sich dort die Entwicklung abzeichnet (Rueckmeldung 20.10.2026). Hat
 * die KI zwoelf Monate gemessen, gibt es das als dritte Sicht.
 */
function Verlauf({ p }: { p: Punkt }) {
  const [sicht, setSicht] = useState<Sicht>('jePeriode')
  const v = p.verlauf!
  const quartal = v.saeulen.some((s) => s.name.startsWith('Q'))
  const wahlen: { wert: Sicht; name: string }[] = v.sichten
    ? [
        { wert: 'jePeriode', name: quartal ? 'Je Quartal' : 'Je Monat' },
        { wert: 'aufgelaufen', name: 'Aufgelaufen seit Januar' },
        ...(v.messung === 'zwoelf_monate' ? [{ wert: 'gemessen' as const, name: 'Letzte 12 Monate' }] : []),
      ]
    : []
  const daten: SaeulenDaten =
    v.sichten && sicht !== 'gemessen' ? { einheit: v.einheit, saeulen: sicht === 'jePeriode' ? v.sichten.jePeriode : v.sichten.aufgelaufen } : v
  const wert = !v.sichten ? VERLAUF_ART[v.art] : sicht === 'jePeriode' ? (quartal ? 'Quartalswert' : 'Monatswert') : sicht === 'aufgelaufen' ? 'aufgelaufen seit Januar' : '12 Monate bis dahin'
  return (
    <section className="punktansicht-teil">
      <div className="bericht-teil-kopf">
        <h4>Verlauf: {p.messpunkt!.name}</h4>
        {wahlen.length > 0 && (
          <div className="umschalter" role="group" aria-label="Sicht">
            {wahlen.map((w) => (
              <button key={w.wert} type="button" aria-pressed={w.wert === sicht} onClick={() => setSicht(w.wert)}>
                {w.name}
              </button>
            ))}
          </div>
        )}
      </div>
      <Legende daten={daten} wert={wert} aktuell="Stand dieses Berichts" />
      <Saeulen daten={daten} hoehe={200} beschriftung={`Verlauf ${p.messpunkt!.name}`} />
    </section>
  )
}

export function Punktansicht({
  punkt,
  nr,
  anzahl,
  vorperiode,
  zurueck,
  vorher,
  weiter,
}: {
  punkt: Punkt
  /** "Vormonat" oder "Vorquartal" */
  vorperiode: string
  nr: number
  anzahl: number
  zurueck: () => void
  /** Nachbarpunkte in Lesereihenfolge, ueber Kapitel hinweg; der Titel steht am Pfeil. */
  vorher: { titel: string; gehen: () => void } | null
  weiter: { titel: string; gehen: () => void } | null
}) {
  const p = punkt
  return (
    <article className="punktansicht">
      <div className="punktansicht-leiste">
        <button type="button" className="verweisknopf" onClick={zurueck}>
          ‹ Übersicht
        </button>
        <span className="nebensache">
          {gruppeName(p.gruppe, vorperiode)} · {nr} von {anzahl}
        </span>
        <span className="punktansicht-blaettern">
          <button
            type="button"
            className="knopf pfeilknopf"
            aria-label={vorher ? `Vorheriger Punkt: ${vorher.titel}` : 'Vorheriger Punkt'}
            title={vorher ? `Zurück: ${vorher.titel}` : undefined}
            disabled={!vorher}
            onClick={vorher?.gehen}
          >
            ‹
          </button>
          <button
            type="button"
            className="knopf pfeilknopf"
            aria-label={weiter ? `Nächster Punkt: ${weiter.titel}` : 'Nächster Punkt'}
            title={weiter ? `Weiter: ${weiter.titel}` : undefined}
            disabled={!weiter}
            onClick={weiter?.gehen}
          >
            ›
          </button>
        </span>
      </div>

      <div className="punktansicht-meta">
        <span className={`prio ${p.prioritaet}`} aria-hidden="true" />
        {gruppeName(p.gruppe, vorperiode)} · Priorität {p.prioritaet} · {stand(p, vorperiode)}
        {p.vorlaeufig && ' · vorläufig'}
      </div>
      <h3 className="punktansicht-titel">{p.titel}</h3>
      {p.stand ? (
        <>
          <p className="punktansicht-text">{p.stand.lage ?? p.stand.zeile ?? p.text}</p>
          {p.stand.lage && p.stand.zeile && <p className="punktansicht-zahlen nebensache">{p.stand.zeile}</p>}
        </>
      ) : (
        <p className="punktansicht-text">{p.text}</p>
      )}

      {p.verlauf && p.messpunkt ? (
        <Verlauf p={p} />
      ) : (
        p.ohneVerlauf && <p className="nebensache punktansicht-teil">{p.ohneVerlauf}</p>
      )}

      {p.stand ? (
        <section className="punktansicht-damals">
          <h4>Beurteilung von {p.stand.beurteilt}</h4>
          <p className="nebensache">So stand es im Bericht {p.stand.beurteilt}, mit den Zahlen von damals.</p>
          <p className="punktansicht-damals-text">{p.text}</p>
          <Belege belege={p.belege} />
        </section>
      ) : (
        p.belege.length > 0 && (
          <section className="punktansicht-teil">
            <h4>Zahlen dazu</h4>
            <Belege belege={p.belege} />
          </section>
        )
      )}
    </article>
  )
}

function Belege({ belege }: { belege: Punkt['belege'] }) {
  if (belege.length === 0) return null
  return (
    <table className="punktansicht-belege">
      <tbody>
        {belege.map((b, i) => (
          <tr key={i}>
            <td>{b.was}</td>
            <td className="nebensache">{b.bezug}</td>
            <td className="zahl">{b.wert}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
